<template>
  <div class="app-header--wrapper" ref="wrapper">
    <div class="app-header-scroll-anchor" ref="anchor" />
    <div class="app-header padding-to-lg" :class="containerClasses" v-if="headerData">
      <div class="container">
        <div class="row">
          <div class="col-xs-6 col-sm-3 col-md-2">
            <NuxtLink
              :to="ROOT_PATH"
              class="app-header__logo-link"
              @click="(event) => sendTrackingEvent({ label: 'Logo', link: ROOT_PATH })"
              aria-label="ArtNight"
            >
              <img
                alt="ArtNight"
                :src="headerData.logoBlack"
                :class="{
                  'app-header__logo': true,
                  'app-header__logo--large': isHomepage
                }"
              />
            </NuxtLink>
          </div>
          <div class="col-xs-6 col-sm-9 col-md-10 app-header__menu">
            <MainMenu :items="menuItems" @selectedMenueItemVal="sendTrackingEvent($event)" />
            <div class="app-header__menu-icons">
              <div class="app-header__user">
                <UserIcon class="app-header__user-icon" @click="onUserIconClick" aria-label="User account" />
              </div>
              <div
                :class="{
                  'app-header__cart': true,
                  'app-header__cart--count': totalQuantity > 0
                }"
                :style="`--cart-quantity: ${totalQuantity}`"
              >
                <CartIcon v-on="microcartListeners" class="app-header__cart-icon" aria-label="Shopping cart">
                </CartIcon>
              </div>
              <BurgerIcon @click="toggleSideNav" class="app-header__burger"> </BurgerIcon>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Sidebar :show-logo="true" state-key="isMobileMenuOpen">
      <template #content>
        <Transition name="fade">
          <SidebarMenu
            v-if="uiStore.state.isMobileMenuOpen"
            class="n-header__side-menu"
            :items="menuItems"
            @click="sendTrackingEvent($event)"
          />
        </Transition>
      </template>
    </Sidebar>
  </div>
</template>

<script lang="ts" setup>
import { computed, reactive, onMounted, onBeforeUnmount, defineAsyncComponent, watch } from 'vue';
import debounce from 'lodash-es/debounce';
import { useStoryblokStore } from '@/stores/storyblok';
import { useUiStore } from '@/stores/ui';
import { storeToRefs } from 'pinia';
import { cartGetters } from '@/resources/getters';
import { useCartStore } from '@/stores/cart';
import { useUserStore } from '@/stores/user';
import { useNuxtApp } from '#imports';
import { sendButtonClickedEvent } from '@/resources/tracking';
import { CHECKOUT_PATH, ROOT_PATH, MOTIV_SLUG_MALEN_ID, EVENTS_SLUG } from '@/resources/helpers';

const MainMenu = defineAsyncComponent(() => import('@/components/Header/SMenu.vue'));
const Microcart = defineAsyncComponent(() => import('@/components/Header/Microcart.vue'));
const SidebarMenu = defineAsyncComponent(() => import('@/components/Header/SidebarMenu.vue'));
const Sidebar = defineAsyncComponent(() => import('@/components/Header/Sidebar.vue'));
const BurgerIcon = defineAsyncComponent(() => import('@/assets/icons/icon-burger-menu.svg?component'));
const CartIcon = defineAsyncComponent(() => import('@/assets/icons/icon-cart.svg?component'));
const UserIcon = defineAsyncComponent(() => import('@/assets/icons/icon--user.svg?component'));

const props = defineProps({
  theme: {
    type: String,
    default: 'dark'
  },
  reduced: {
    type: Boolean,
    default: false
  }
});

defineOptions({
  inheritAttrs: false
});

const { $grid, $router } = useNuxtApp();
const storyblokStore = useStoryblokStore();
const uiStore = useUiStore();
const { isCartSidebarOpen } = storeToRefs(uiStore);

const cartStore = useCartStore();
const userStore = useUserStore();
const { isUserLoggedIn, user } = storeToRefs(userStore);

const { cart } = storeToRefs(cartStore);

const selectedItem = ref({});
const selectedLabel = ref('');

const state = reactive({
  isSidebarActive: false,
  isSticky: false,
  isHidden: false,
  prevScrollPos: 0
});

const wrapper = ref(null);
const anchor = ref(null);

const noop = () => {};
const isMobile = computed(() => $grid.breakpoint === 'xs' || $grid.breakpoint === 'sm');

const isHomepage = computed(() => $router.currentRoute.value.path === ROOT_PATH);

const isProductPage = computed(
  () =>
    String($router.currentRoute.value.name).includes(EVENTS_SLUG) ||
    String($router.currentRoute.value.name) === MOTIV_SLUG_MALEN_ID
);

const headerData = computed(() => {
  if (storyblokStore.globalComponents.header && Object.keys(storyblokStore.globalComponents.header).length > 0) {
    return storyblokStore.globalComponents.header;
  }
  return null;
});

const menuItems = computed(() => headerData?.value?.navigation?.items || []);

const totalQuantity = computed(() => cartGetters.getCartTotalItems(cart.value));
const handleScroll = () => {
  const currentScrollPos = window.pageYOffset;
  state.isHidden =
    currentScrollPos > state.prevScrollPos && currentScrollPos > wrapper.value.offsetTop + wrapper.value.offsetHeight;

  state.prevScrollPos = currentScrollPos;
};
const debouncedHandleScroll = debounce(handleScroll, 25);

function sendTrackingEvent(event: any) {
  let buttonAction, elementId;
  if (event.label === 'Burger' || event.label === 'Städte') {
    buttonAction = state.isSidebarActive ? 'open' : !$grid.lg && !state.isSidebarActive ? 'close' : 'menu';
    elementId = selectedItem.value?.target?.id || event?.target?.id || undefined;
  }
  if (!event.link || !event.label) {
    event.link = $router.currentRoute.value?.fullPath;
  }

  sendButtonClickedEvent({
    targetPath: event.link,
    targetUrl: window.location.origin + event.link,
    pagePath: $router.currentRoute.value.fullPath,
    pageUrl: window.location.origin + $router.currentRoute.value?.fullPath,
    label: event.label ?? event.target?.innerText,
    pageTitle: event.label,
    pageType: $router.currentRoute.value.name,
    elementId,
    locationCity: selectedLabel?.value ? [selectedLabel?.value] : undefined,
    buttonAction
  });
}

function toggleSideNav() {
  state.isSidebarActive = !state.isSidebarActive;
  uiStore.toggleState('isMobileMenuOpen');

  sendTrackingEvent({ label: 'Burger', link: 'buger' });
}

onMounted(() => {
  if (import.meta.server) return;
  window.addEventListener('scroll', debouncedHandleScroll);
  state.prevScrollPos = window.pageYOffset;

  const ob = new IntersectionObserver((entries) => {
    state.isSticky = !entries[0].isIntersecting;
  });
  ob.observe(anchor.value);
});

onBeforeUnmount(() => {
  if (import.meta.server) return;
  window.removeEventListener('scroll', debouncedHandleScroll);
});

const isCheckoutRedirect = computed(
  () => $grid && ($grid.md || (!$grid.md && totalQuantity.value > 0 && !isProductPage.value))
);

// const microcartWrapperListeners = computed(() => ({
//   mouseover: ($grid && !$grid.md) || isCartSidebarOpen.value ? noop : uiStore.openCartSidebar,
//   mouseleave: $grid && !$grid.md ? noop : uiStore.closeCartSidebar
// }));
const microcartListeners = computed(() => ({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  click: !isCheckoutRedirect.value ? uiStore.toggleCartSidebar : uiStore.openCartSidebar
}));

const containerClasses = computed(() => {
  return {
    'app-header--hidden': state.isHidden,
    'app-header--scrolling': state.isSticky
  };
});

function onUserIconClick() {
  uiStore.toggleState('isAuthSidebarOpen');
  if (user.value !== null && isUserLoggedIn.value) {
    uiStore.toggleState('isLogoutComponentVisible', true);
  } else {
    uiStore.toggleState('isLoginComponentVisible', true);
  }
}
const cartItems = computed(() => cart.value?.items);
// missing data for cartIcon as currently having issues with Tyk
// need to add microcart tracking but currently bug not allowing to have items in cart
</script>

<style lang="scss" scoped>
@use '@/assets/styles/css_BASE/helpers/mixins/_bp.scss' as *;

.app-header {
  transform: translateY(0);
  transition: transform 0.3s ease-in-out, padding 0.3s ease-in-out;

  &--wrapper {
    position: relative;
    min-height: 85px;
    z-index: 13;

    @include bp(lg) {
      min-height: 115.5px;
    }
  }

  &__menu-icons {
    display: flex;
  }

  &__user {
    width: 45px;
  }

  &__user-icon {
    margin: 7px 27px 0 0;
    cursor: pointer;
  }

  &__cart-icon {
    cursor: pointer;
  }

  &--scrolling {
    position: fixed;
    width: 100%;
    top: 0;
    z-index: 99;
    background: #fff;
    padding: 20px;
  }

  &--hidden {
    transform: translateY(-85px);

    @include bp(lg) {
      transform: translateY(-92px);
    }
  }
}
</style>
